import { useMemo, useState } from 'react';
import Reveal from '@/components/base/Reveal';
import SectionTitle from '@/components/base/SectionTitle';
import { episodes, episodePhases } from '@/mocks/drama';

export default function EpisodeSection() {
  const [activePhase, setActivePhase] = useState<string>('全部');

  const tabs = useMemo(() => ['全部', ...episodePhases], []);
  const visibleEpisodes = useMemo(
    () => (activePhase === '全部' ? episodes : episodes.filter((item) => item.phase === activePhase)),
    [activePhase]
  );

  return (
    <section id="episodes" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto px-4 md:px-6 max-w-7xl">
        <Reveal>
          <SectionTitle
            eyebrow="全集导览"
            title="全集分集导览：从第 1 集到大结局"
            description="全剧按四个篇章推进，共整理 18 集的可浏览看点。你可以用下方标签按篇章筛选，快速找到想回顾的段落，也可以从第一集顺着看到大结局。"
          />
        </Reveal>

        <Reveal delay={60}>
          <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="按篇章筛选剧集">
            {tabs.map((tab) => {
              const active = tab === activePhase;
              return (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActivePhase(tab)}
                  className={`rounded-full px-4 py-2 font-label text-sm transition-colors cursor-pointer ${
                    active
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
                  }`}
                >
                  {tab}
                  {tab !== '全部' ? (
                    <span className="ml-1 text-xs opacity-80">
                      {episodes.filter((item) => item.phase === tab).length}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </Reveal>

        <div className="mt-6 grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {visibleEpisodes.map((ep, index) => (
            <Reveal key={ep.id} delay={(index % 3) * 50}>
              <article className="h-full rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:border-accent-300">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 font-heading text-sm font-bold text-primary-800">
                      {ep.id}
                    </span>
                    <span className="font-label text-xs text-secondary-700">{ep.phase}</span>
                  </span>
                  <span className="flex items-center gap-1 font-label text-xs text-foreground-500">
                    <i className="ri-time-line" aria-hidden="true" />
                    {ep.duration}
                  </span>
                </div>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{ep.title}</h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-foreground-700">{ep.summary}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {ep.tags.map((tag) => (
                    <span key={tag} className="rounded-full border border-background-300 px-2.5 py-0.5 font-label text-xs text-foreground-600">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}